import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { env } from './env'
import { createAuthGuardedFetch } from './authGuardedFetch'
import type { Database } from './database.types'

// `<Database>` wires in the auto-generated types from
// `lib/database.types.ts` — every `.from(...)`, `.rpc(...)`, column name,
// etc. is now type-checked against the real Postgres schema. Regenerate
// after migrations with `npm run types:gen`.
//
// `global.fetch` is wrapped by `createAuthGuardedFetch` so that when an
// expired / revoked session produces a 401/403 from Supabase, we trigger
// `supabase.auth.signOut()` once. The existing `onAuthStateChange`
// listener in `AuthProvider` then transitions to the login screen,
// preventing the cascade of red queries that would otherwise ensue.
// See ADR 0007 for rationale + behaviour.
//
// The `onAuthFailure` callback closes over `supabase` via a named function
// so the lexical reference resolves at call time (not init time) — by the
// time any request fires, the export has bound. Static type order is
// irrelevant.
// Explicit type annotation breaks the circular-inference loop caused by
// `onAuthFailure` closing over `supabase` (TS7022/7023 in a -b build). The
// value is otherwise unchanged.
export const supabase: SupabaseClient<Database> = createClient<Database>(
  env.supabase.url,
  env.supabase.anonKey,
  {
    global: {
      fetch: createAuthGuardedFetch({
        baseFetch: (...args) => fetch(...args),
        onAuthFailure: () => supabase.auth.signOut(),
      }),
    },
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  }
)
